/**
 * leadsDashboard.js - the pure rules behind the Leads Dashboard (D-01..D-06,
 * Paul's Phase 1.2 list): the period presets, the four KPI counts, stage
 * movement, the Cold summary, source performance by cohort and the two
 * attention lists.
 *
 * No React and no fetching. views/LeadsDashboardView hands in the accounts
 * (each carries its own stageHistory and statusHistory from GET /api/accounts),
 * the opportunities, and the lead cards leadsBoard.buildLeadCards made - so
 * every "live" number here is exactly the Leads board's under the same filters.
 *
 * Definitions (lead defaults, r43 contract Decision 17):
 *   - A unit is a site, or an account with no sites (crmStages.isBoardUnit),
 *     of ANY status or designation, so a lead that later went Cold, Dead or
 *     Customer still counts for what it did in the period - except one made
 *     automatically for an opportunity (auto_created, 10/12/26), never a lead,
 *     and an Account (designation Account, Paul 10/21/26: tracked, not in the
 *     lead pipeline - even once a win has made it a Customer).
 *   - Stage history keeps the OLD stage names (the 9/28 migration left it
 *     alone): historyStage maps them. A row that maps to null never counts as
 *     an entry, but it still ends the stint before it.
 *   - Entries and stints made while the stored designation was Customer are
 *     skipped: customers are not leads.
 *   - Every day is a LOCAL 'YYYY-MM-DD' - never toISOString's UTC day, which
 *     turns "today" into tomorrow after 8pm Eastern.
 *
 * __tests__/leadsDashboard.test.js pins every rule.
 */
import {
  CRM_STAGES,
  LEAD_SOURCES,
  TIERS,
  VERTICALS,
  stageRank,
  normalizeTier,
  verticalOf,
  leadStatusOf,
  designationOf,
  isBoardUnit,
  isAutoCreated,
  isNotLeadDesignation,
  pickField,
} from '../../data/crmStages';
import { isProjectRow } from '../../data/projectStatus';
import { fmtDate } from '../../utils/format';
import { boardColumn } from './leadsBoard';
import { accountLabel, indexById } from './nextStep';
import { localDay as ymd } from '../../utils/localDay';

/* ── periods (D-01) ──────────────────────────────────────────────────────── */

export const DASH_PERIODS = [
  { value: 'week', label: 'Last week' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'custom', label: 'Custom' },
];
export const DEFAULT_PERIOD = '30d';
export const periodLabel = (key) => (DASH_PERIODS.find((p) => p.value === key) || {}).label || '';

/** D-06: "Stalled" means MORE than this many days in the current stage. */
export const STALL_DAYS = 45;
/** Status changes (designation, Cold / Active) have only been recorded since the 9/28/26 release. */
export const STATUS_HISTORY_SINCE = '2026-09-28';

export const ALL = 'All';
/** The filter value that matches a blank field (a lead with no lead source, say). */
export const NONE = '(none)';
export const DEFAULT_DASH_FILTERS = { owner: ALL, tier: ALL, vertical: ALL, source: ALL };

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86400000;
const pad = (n) => String(n).padStart(2, '0');

/**
 * The local calendar day of a value: a 'YYYY-MM-DD' passes through untouched
 * (new Date('2026-09-01') would be UTC midnight, the day before in the US); a
 * Date or an ISO timestamp becomes its local day. '' when blank or invalid.
 */
export function localDay(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'string' && DAY_RE.test(v.trim())) return v.trim();
  const dt = v instanceof Date ? v : new Date(v);
  return isNaN(dt.getTime()) ? '' : ymd(dt);
}

// noon, so adding days never trips over a daylight-saving change
const atNoon = (day) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};

/** A local day plus n days (n may be negative). */
export function addDays(day, n) {
  const dt = atNoon(day);
  dt.setDate(dt.getDate() + n);
  return ymd(dt);
}

/**
 * The period as { from, to } local days, both inclusive.
 *   week     the previous Monday to Sunday (a weekly review looks at last week)
 *   30d      today - 29 .. today
 *   quarter  this calendar quarter to date
 *   ytd      January 1 .. today
 *   custom   custom.from .. custom.to; swapped when reversed, a blank end is
 *            today and a blank start is the end
 * An unknown key falls back to the last 30 days.
 */
export function periodRange(key, today = new Date(), custom = {}) {
  const t = localDay(today) || localDay(new Date());
  const [y, m] = t.split('-').map(Number);
  switch (key) {
    case 'week': {
      const dow = atNoon(t).getDay(); // 0 = Sunday
      const monday = addDays(t, -((dow + 6) % 7)); // this week's Monday
      return { from: addDays(monday, -7), to: addDays(monday, -1) };
    }
    case 'quarter':
      return { from: `${y}-${pad(Math.floor((m - 1) / 3) * 3 + 1)}-01`, to: t };
    case 'ytd':
      return { from: `${y}-01-01`, to: t };
    case 'custom': {
      let to = localDay((custom || {}).to) || t;
      let from = localDay((custom || {}).from) || to;
      if (from > to) [from, to] = [to, from];
      return { from, to };
    }
    default:
      return { from: addDays(t, -29), to: t };
  }
}

export const inPeriod = (v, r) => {
  const d = localDay(v);
  return !!d && !!r && d >= r.from && d <= r.to;
};

/** "09/21/2026 - 09/27/2026", or the one date when the period is a single day. */
export const fmtRange = (r) => (!r ? '' : r.from === r.to ? fmtDate(r.from) : `${fmtDate(r.from)} - ${fmtDate(r.to)}`);

/* ── stage history ───────────────────────────────────────────────────────── */

/*
 * Stage-history name -> today's stage. The same renames as the 9/28/26
 * migration (leadsBoard.js LEGACY_COLUMN) but stricter than boardColumn: 'Target - no
 * contact' and 'Contact attempted' were not leads yet (a target becomes a
 * lead when it replies), 'RFQ received' depended on the NDA and 'Active
 * customer' was a customer - all four map to null.
 */
const HISTORY_STAGE = new Map([
  ...CRM_STAGES.map((s) => [s, s]),
  ['Discovery meeting complete', 'Discovery Meeting Complete'],
  ['NDA sent', 'NDA In-Process'],
  ['NDA executed', 'NDA Executed'],
]);
export const historyStage = (s) => HISTORY_STAGE.get(String(s == null ? '' : s).trim()) || null;

/** Median of the numbers; null for none. */
export function median(xs) {
  const v = (xs || []).filter((x) => typeof x === 'number' && isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

/* ── units ───────────────────────────────────────────────────────────────── */

const push = (m, k, v) => {
  if (!m.has(k)) m.set(k, []);
  m.get(k).push(v);
};

/*
 * History rows oldest first, each with its instant (ms) and local day. The
 * API sends them newest first (stage rows with no tiebreak, status rows with
 * id DESC); reversing before the stable sort keeps same-instant rows in the
 * order they were written.
 */
function ascending(rows, shape) {
  return [...(rows || [])]
    .reverse()
    .map((r) => {
      const at = pickField(r, 'changedAt', 'changed_at');
      const ms = at ? new Date(at).getTime() : NaN;
      return { ...shape(r), at, ms, day: localDay(at) };
    })
    .filter((r) => isFinite(r.ms))
    .sort((x, y) => x.ms - y.ms);
}

/** The stored designation in force at an instant: the latest designation change at or before it, else Lead. */
function designationAt(statusRows, ms) {
  let d = 'Lead';
  for (const r of statusRows) {
    if (r.ms > ms) break;
    if (r.field === 'designation' && r.to) d = r.to;
  }
  return d;
}

/** The same, for a whole local day (every change made on or before that day). */
function designationOnDay(statusRows, day) {
  let d = 'Lead';
  for (const r of statusRows) {
    if (r.day > day) break;
    if (r.field === 'designation' && r.to) d = r.to;
  }
  return d;
}

/**
 * An opportunity's day (Paul: the first opportunity CREATED on a Lead site).
 * One added in the app (isUserCreated) is dated by the local day it was
 * created, so an opportunity added today with an older quote date counts
 * today and never changes a past period. A row loaded by an import (before
 * 10/1/26) or by the seed has the import time as its created_at, so it is
 * dated by its quote date, else the day it was added.
 */
export function oppDay(p) {
  const created = localDay(pickField(p, 'createdAt', 'created_at'));
  if (pickField(p, 'isUserCreated', 'is_user_created') === true && created) return created;
  const q = String(pickField(p, 'quoteDate', 'quote_date') || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(q)) return q.slice(0, 10);
  return localDay(q) || created;
}

const excludedFromTotals = (p) => !!pickField(p, 'excludedFromTotals', 'excluded_from_totals');

// minutes the local clock moved between two instants (a daylight-saving change)
const offsetShiftMs = (fromMs, toMs) => (new Date(toMs).getTimezoneOffset() - new Date(fromMs).getTimezoneOffset()) * 60000;

/*
 * Entries: a row whose stage differs from the row before it (a rename such as
 * 'NDA sent' -> 'NDA In-Process' is not a move). Stints: runs of the same
 * stage; a stint ends at the next row with a different stage - including a
 * null one - and only completed stints have a length. A stint counts only
 * when it began AND ended while the record was a Lead, so a lead that became
 * a Customer mid-stint does not add its customer months to the median.
 * A stint's length is whole days of elapsed time on the LOCAL clock: the hour
 * a daylight-saving change adds or takes away is corrected, so 10:00 on
 * 03/02/2027 to 10:00 on 03/16/2027 is 14 days, not 13.
 */
function entriesAndStints(stageRows, statusRows) {
  const asLead = (ms) => designationAt(statusRows, ms) === 'Lead';
  const entries = [];
  const stints = [];
  let prev = null;
  for (const r of stageRows) {
    if (r.stage && r.stage !== prev && asLead(r.ms)) entries.push({ stage: r.stage, day: r.day, ms: r.ms });
    prev = r.stage;
  }
  let i = 0;
  while (i < stageRows.length) {
    const start = stageRows[i];
    let j = i + 1;
    while (start.stage && j < stageRows.length && stageRows[j].stage === start.stage) j++;
    const end = stageRows[j];
    if (start.stage && end && asLead(start.ms) && asLead(end.ms)) {
      const days = Math.floor((end.ms - start.ms - offsetShiftMs(start.ms, end.ms)) / DAY_MS);
      stints.push({ stage: start.stage, exitDay: end.day, days });
    }
    i = j;
  }
  return { entries, stints };
}

/**
 * Every unit with what the dashboard needs, computed once:
 *   { id, label, name, isSite, owner, tier, vertical, leadSource, status,
 *     designation, createdDay, seeded, createdAsCustomer, stageRows,
 *     statusRows, entries, stints, opps, firstOppDay, firstOppAsLead, won,
 *     column, furthest, record }
 * Opportunities are the ones linked to the unit itself (projects.account_id,
 * the server's linkedOpportunityIds), never name matches - an old customer's
 * quotes must not credit a brand-new site. Excluded-from-totals rows never count.
 */
export function dashUnits(accounts, projects) {
  const list = (accounts || []).filter((a) => a && a.id != null);
  const byId = indexById(list);
  const projById = indexById(projects);
  const linkedTo = new Map();
  for (const p of projects || []) {
    const acct = p && pickField(p, 'accountId', 'account_id');
    if (acct == null || acct === '' || excludedFromTotals(p)) continue;
    push(linkedTo, String(acct), p);
  }
  // an account made automatically for an opportunity (auto_created) was never
  // a lead: it counts nowhere here until someone tracks it as one; nor does an
  // Account (10/21/26) - Paul's Cold workaround stops skewing the Cold counts -
  // nor a Target (T-0010, 11/3/26)
  return list
    .filter((a) => isBoardUnit(a) && !isAutoCreated(a) && !isNotLeadDesignation(a))
    .map((a) => {
      const id = String(a.id);
      const stageRows = ascending(pickField(a, 'stageHistory', 'stage_history'), (r) => ({
        raw: r.stage == null ? null : r.stage,
        stage: historyStage(r.stage),
      }));
      const statusRows = ascending(pickField(a, 'statusHistory', 'status_history'), (r) => ({
        field: r.field,
        from: pickField(r, 'fromValue', 'from_value') ?? null,
        to: pickField(r, 'toValue', 'to_value') ?? null,
        by: pickField(r, 'changedBy', 'changed_by') || '',
      }));
      const { entries, stints } = entriesAndStints(stageRows, statusRows);

      const seen = new Map();
      for (const pid of pickField(a, 'linkedOpportunityIds', 'linked_opportunity_ids') || []) {
        const p = projById.get(String(pid));
        if (p && !excludedFromTotals(p)) seen.set(String(p.id), p);
      }
      for (const p of linkedTo.get(id) || []) seen.set(String(p.id), p);
      const opps = [...seen.values()];
      const days = opps.map(oppDay).filter(Boolean).sort();
      const firstOppDay = days[0] || '';

      const column = boardColumn(a);
      const furthest = Math.max(stageRank(column), ...stageRows.map((r) => stageRank(r.stage)));
      return {
        id,
        label: accountLabel(a, byId),
        name: a.name || '',
        isSite: !!pickField(a, 'parentAccountId', 'parent_account_id'),
        owner: a.owner || '',
        tier: normalizeTier(pickField(a, 'tierEffective', 'tier_effective') || a.tier),
        vertical: verticalOf(a) || '',
        leadSource: pickField(a, 'leadSource', 'lead_source') || '',
        status: leadStatusOf(a),
        designation: designationOf(a),
        createdDay: localDay(pickField(a, 'createdAt', 'created_at')),
        // not created as a lead: loaded by the retired seed import, or made by
        // the 10/11/26 cleanup for an old opportunity's customer (is_user_created FALSE)
        seeded: pickField(a, 'isUserCreated', 'is_user_created') === false,
        // POST writes a designation row with no from_value when a record starts as a Customer
        createdAsCustomer: statusRows.some((r) => r.field === 'designation' && r.from == null && r.to === 'Customer'),
        // ...or as an Account (10/21/26): not a new lead the day it was made
        createdAsAccount: statusRows.some((r) => r.field === 'designation' && r.from == null && r.to === 'Account'),
        stageRows,
        statusRows,
        entries,
        stints,
        opps,
        firstOppDay,
        firstOppAsLead: !!firstOppDay && designationOnDay(statusRows, firstOppDay) === 'Lead',
        won: opps.some(isProjectRow),
        column,
        furthest,
        record: a,
      };
    });
}

/* ── filters (D-01) ──────────────────────────────────────────────────────── */

const wants = (picked, value) => picked == null || picked === '' || picked === ALL || (picked === NONE ? !value : value === picked);

/**
 * One unit OR one lead card against the Owner / Tier / Vertical / Lead source
 * filters ('All' = any, '(none)' = blank). Both carry the same field names, so
 * the period numbers and the live numbers filter alike. `nameOf` turns an
 * owner into the name shown (an email owner reads as its person).
 */
export function matchesDash(u, f = {}, nameOf = (o) => o) {
  if (!u) return false;
  return (
    wants(f.owner, nameOf(u.owner || '') || '') &&
    wants(f.tier, u.tier || '') &&
    wants(f.vertical, u.vertical || '') &&
    wants(f.source, u.leadSource || '')
  );
}

const alpha = (x, y) => x.localeCompare(y);
/** Known values in their list's order (all of them, or only those in use), then unknown ones A-Z, then (none). */
function optionList(known, values, { allKnown = false, none = 'used' } = {}) {
  const used = new Set(values.filter(Boolean));
  const head = known.filter((k) => allKnown || used.has(k));
  const other = [...used].filter((v) => !known.includes(v)).sort(alpha);
  const blank = none === 'always' || (none === 'used' && values.some((v) => !v));
  return [ALL, ...head, ...other, ...(blank ? [NONE] : [])];
}

/** The choices for each filter, from every unit (any status), so they hold still as filters change. */
export function dashFilterOptions(units, nameOf = (o) => o) {
  const list = units || [];
  return {
    owners: optionList(
      [],
      list.map((u) => nameOf(u.owner || '') || '')
    ),
    tiers: optionList(
      TIERS,
      list.map((u) => u.tier || ''),
      { allKnown: true }
    ),
    verticals: optionList(
      VERTICALS,
      list.map((u) => u.vertical || '')
    ),
    // every source Paul evaluates is offered even at zero (InsideJob, LinkedIn Ads, GushWork)
    sources: optionList(
      LEAD_SOURCES,
      list.map((u) => u.leadSource || ''),
      { allKnown: true, none: 'always' }
    ),
  };
}

/** Stored preferences, made safe: an unknown period or a bad date falls back to the default. */
export function sanitizeDashPrefs(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  const str = (v) => (typeof v === 'string' && v.trim() ? v : ALL);
  const day = (v) => (typeof v === 'string' && DAY_RE.test(v) ? v : '');
  return {
    period: DASH_PERIODS.some((p) => p.value === o.period) ? o.period : DEFAULT_PERIOD,
    from: day(o.from),
    to: day(o.to),
    owner: str(o.owner),
    tier: str(o.tier),
    vertical: str(o.vertical),
    source: str(o.source),
  };
}

/* ── D-02 KPI cards ──────────────────────────────────────────────────────── */

/** New in the period: created as a lead (not as a Customer, not by the seed import). */
export const isNewLead = (u, range) => !u.seeded && !u.createdAsCustomer && !u.createdAsAccount && inPeriod(u.createdDay, range);

const enteredIn = (u, stage, range) => u.entries.some((e) => e.stage === stage && inPeriod(e.day, range));

/** Units that entered `stage` in the period - each unit once, however often its card moved. */
export const unitsEntering = (units, stage, range) => (units || []).filter((u) => enteredIn(u, stage, range));

/**
 * D-02: { newLeads, meetings, ndas, firstOpps } as counts, plus `lists` with
 * the units behind each so the page can show who was counted.
 */
export function kpiCounts(units, range) {
  const list = units || [];
  const lists = {
    newLeads: list.filter((u) => isNewLead(u, range)),
    meetings: unitsEntering(list, 'Discovery Meeting Complete', range),
    ndas: unitsEntering(list, 'NDA Executed', range),
    firstOpps: list.filter((u) => u.firstOppAsLead && inPeriod(u.firstOppDay, range)),
  };
  return {
    newLeads: lists.newLeads.length,
    meetings: lists.meetings.length,
    ndas: lists.ndas.length,
    firstOpps: lists.firstOpps.length,
    lists,
  };
}

/* ── D-03 stage movement ─────────────────────────────────────────────────── */

/** Entry EVENTS into `stage` in the period: a card moved into it twice counts twice. */
const entriesInto = (units, stage, range) =>
  (units || []).reduce((n, u) => n + u.entries.filter((e) => e.stage === stage && inPeriod(e.day, range)).length, 0);

/**
 * One row per stage: { stage, entered, now, medianDays, n }.
 *   entered     entries into the stage in the period - every move counts, so
 *               dragging a card in again adds one (Paul's D-03 pass: "Drag a
 *               card: entered increments"); the D-02 cards count each lead once
 *   now         Active lead cards in that board column right now (live)
 *   medianDays  median whole days of the completed stints whose EXIT falls in
 *               the period (null when there are none); n = how many stints
 * `cards` must already be filtered like `units`.
 */
export function stageMovement(units, cards, range) {
  const list = units || [];
  return CRM_STAGES.map((stage) => {
    const durations = list.flatMap((u) => u.stints.filter((s) => s.stage === stage && inPeriod(s.exitDay, range)).map((s) => s.days));
    const m = median(durations);
    return {
      stage,
      entered: entriesInto(list, stage, range),
      now: (cards || []).filter((c) => c.status === 'Active' && c.column === stage).length,
      medianDays: m == null ? null : Math.round(m),
      n: durations.length,
    };
  });
}

/* ── D-04 Cold summary ───────────────────────────────────────────────────── */

/**
 * { wentCold, reactivated, coldNow, events: { wentCold: [], reactivated: [] } }.
 * Counts status-change EVENTS in the period (a lead that went Cold twice counts
 * twice), made while the record was a Lead. Went Cold includes a record created
 * Cold (no from value); reactivated is Cold -> Active only (Cold -> Inactive is
 * not). coldNow is live: the Cold lead cards, the Leads page's Cold toggle.
 */
export function coldSummary(units, cards, range) {
  const events = { wentCold: [], reactivated: [] };
  for (const u of units || []) {
    for (const r of u.statusRows) {
      if (r.field !== 'lead_status' || !inPeriod(r.day, range)) continue;
      if (designationAt(u.statusRows, r.ms) !== 'Lead') continue;
      const ev = { id: u.id, label: u.label, day: r.day, from: r.from, to: r.to, by: r.by, ms: r.ms };
      if (r.to === 'Cold' && r.from !== 'Cold') events.wentCold.push(ev);
      else if (r.from === 'Cold' && r.to === 'Active') events.reactivated.push(ev);
    }
  }
  const newestFirst = (x, y) => y.ms - x.ms;
  events.wentCold.sort(newestFirst);
  events.reactivated.sort(newestFirst);
  return {
    wentCold: events.wentCold.length,
    reactivated: events.reactivated.length,
    coldNow: (cards || []).filter((c) => c.status === 'Cold').length,
    events,
  };
}

/* ── D-05 source performance ─────────────────────────────────────────────── */

const DMC_RANK = stageRank('Discovery Meeting Complete');
const NDA_RANK = stageRank('NDA Executed');

const sourceRow = (source, us) => ({
  source,
  total: us.length,
  dmc: us.filter((u) => u.furthest >= DMC_RANK).length,
  nda: us.filter((u) => u.furthest >= NDA_RANK).length,
  withOpp: us.filter((u) => u.opps.length > 0).length,
  won: us.filter((u) => u.won).length,
});

/**
 * D-05, by cohort: the leads created in the period (the New leads set),
 * grouped by lead source. Each lead counts once, under its source, at the
 * furthest point it has reached to date - moving a card back never un-counts
 * it - and the columns are cumulative (a won lead also counts as reaching
 * Discovery Meeting Complete and NDA Executed when it did). Each unit's
 * `furthest` is on it, so an exclusive view would be a one-line change.
 * Rows: LEAD_SOURCES order, unknown old values A-Z, then (none). With
 * `allSources` every known source gets a row, zero or not.
 * Returns { rows: [{ source, total, dmc, nda, withOpp, won }], total }.
 */
export function sourcePerformance(units, range, { allSources = false } = {}) {
  const cohort = (units || []).filter((u) => isNewLead(u, range));
  const groups = new Map();
  for (const u of cohort) push(groups, u.leadSource || NONE, u);
  const known = LEAD_SOURCES.filter((s) => allSources || groups.has(s));
  const other = [...groups.keys()].filter((k) => k !== NONE && !LEAD_SOURCES.includes(k)).sort(alpha);
  const keys = [...known, ...other, ...(groups.has(NONE) ? [NONE] : [])];
  return {
    rows: keys.map((k) => sourceRow(k, groups.get(k) || [])),
    total: sourceRow('Total', cohort),
  };
}

/* ── D-06 attention lists ────────────────────────────────────────────────── */

/** A lead card's "Company - Site" label, the same one the No next step list uses. */
export const cardLabel = (c) => accountLabel({ name: c.name, parentAccountId: c.isSite ? 'site' : null, parentName: c.parentName }, null);
const withLabel = (c) => ({ ...c, label: c.label || cardLabel(c) });
const byLabel = (x, y) => x.label.localeCompare(y.label);

/** Stalled: Active leads MORE than 45 days in their current stage, longest first. No stage date = not listed. */
export const stalledLeads = (cards) =>
  (cards || [])
    .filter((c) => c.status === 'Active' && c.days != null && c.days > STALL_DAYS)
    .map(withLabel)
    .sort((x, y) => y.days - x.days || byLabel(x, y));

/** Active leads the board shows no days for (never staged), so they cannot be judged stalled. */
export const undatedActiveLeads = (cards) => (cards || []).filter((c) => c.status === 'Active' && c.days == null).length;

/**
 * No next step: the lead cards with no open task - the same set as the main
 * Dashboard's T-05 leads (nextStep.recordsWithoutNextStep). A card whose tasks
 * have not loaded has hasTask null and is never listed.
 */
export const noNextStepLeads = (cards) =>
  (cards || [])
    .filter((c) => c.hasTask === false)
    .map(withLabel)
    .sort(byLabel);

/* ── footer ──────────────────────────────────────────────────────────────── */

/** The first local day any stage history was recorded, '' when there is none. */
export function stageHistorySince(accounts) {
  let first = '';
  for (const a of accounts || []) {
    for (const r of pickField(a, 'stageHistory', 'stage_history') || []) {
      const d = localDay(pickField(r, 'changedAt', 'changed_at'));
      if (d && (!first || d < first)) first = d;
    }
  }
  return first;
}
